import React, { memo, useContext, useEffect, useMemo, useState } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box } from '@node-space/ui-components'
import { useWalletQuery } from 'hooks/queries/useWalletQuery'
import { useGetWalletLegacyId } from 'hooks/queries/Wallets/useGetWalletLegacyId'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { SourceWalletTypes } from 'types/move/move'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import {
  selectedContactBankAccountAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from '../Pay/atoms/atoms'
import BeneficiaryForm from './components/BeneficiaryForm'
import { Complete } from './components/Complete'
import { Confirmation } from './components/Confirmation'
import ContactBankAccountForm from './components/ContactBankAccountForm'
import {
  ContactOrBeneficiary,
  ContactOrBeneficiarySelector,
} from './components/ContactOrBeneficiarySelector'
import { PaymentDetails } from './components/PaymentDetails'
import { FiatSendProvider } from './context/FiatSendContext'
import { useFiatSendForm } from './hooks/useFiatSendForm'
import { createSourcePayWallet } from './utils'

const FiatSend = () => {
  const { t } = useTranslation()
  const [walletAction, setWalletAction] = useContext(WalletActionContext)

  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)
  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const [contactBankAccountAtom, setContactBankAccountAtom] = useAtom(
    selectedContactBankAccountAtom
  )

  const [step, setStep] = useState<FiatSendStep>(FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR)

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const { data: walletLegacyId, isFetching: isFetchingWalletLegacyId } = useGetWalletLegacyId(
    walletAction?.sourceWalletReference
  )

  const { data: selectedWallet, isFetching: isFetchingSelectedWallet } = useWalletQuery(
    Number(walletLegacyId),
    !!walletLegacyId && !Number.isNaN(Number(walletLegacyId))
  )

  const isLoadingWallet = useMemo(() => {
    return isFetchingSelectedWallet || isFetchingWalletLegacyId
  }, [isFetchingSelectedWallet, isFetchingWalletLegacyId])

  useEffect(() => {
    if (selectedWallet) {
      const walletType = walletAction?.meta?.customerWallet
        ? SourceWalletTypes.CUSTOMER
        : SourceWalletTypes.MAIN
      const sourceWallet = createSourcePayWallet(selectedWallet, walletType)
      setSourceWalletAtom(sourceWallet)
    }
  }, [selectedWallet, walletAction?.meta?.customerWallet])

  useFiatSendForm()

  const sheetHeader = useMemo(() => {
    switch (step) {
      case FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR:
      case FiatSendSteps.CONTACT_BANK_ACCOUNT:
      case FiatSendSteps.BENEFICIARY:
        return t('wallets.fiatSend.beneficiaryStepHeader')
      case FiatSendSteps.DETAILS:
        return t('wallets.fiatSend.amountStepHeader')
      case FiatSendSteps.CONFIRMATION:
        return t('wallets.fiatSend.confirmationHeader')
      case FiatSendSteps.EOF:
        return null
      default:
        return ''
    }
  }, [step])

  const canGoBack = useMemo(() => {
    switch (step) {
      case FiatSendSteps.BENEFICIARY:
      case FiatSendSteps.CONTACT_BANK_ACCOUNT:
      case FiatSendSteps.DETAILS:
      case FiatSendSteps.CONFIRMATION:
        return true
      case FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR:
      case FiatSendSteps.EOF:
      default:
        return false
    }
  }, [step])

  const handleOnBack = () => {
    if (canGoBack) {
      switch (step) {
        case FiatSendSteps.DETAILS:
          if (contactBankAccountAtom) {
            setStep(FiatSendSteps.CONTACT_BANK_ACCOUNT)
            setContactBankAccountAtom(null)
            return
          }
          setStep(FiatSendSteps.BENEFICIARY)
          setBeneficiaryAtom(null)
          return
        case FiatSendSteps.CONFIRMATION:
          setStep(FiatSendSteps.DETAILS)
          return
        case FiatSendSteps.BENEFICIARY:
        case FiatSendSteps.CONTACT_BANK_ACCOUNT:
          setStep(FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR)
          return
        case FiatSendSteps.EOF:
        default:
      }
    }
  }

  const handleOnContactOrBeneficiaryClick = (value: string) => {
    if (value === ContactOrBeneficiary.CONTACT) {
      setStep(FiatSendSteps.CONTACT_BANK_ACCOUNT)
    } else {
      setStep(FiatSendSteps.BENEFICIARY)
    }
  }

  useEffect(() => {
    setWalletAction({
      ...walletAction,
      header: sheetHeader,
      onBack: canGoBack ? handleOnBack : undefined,
      onClose: () => {
        track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_CLOSE, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.CLICK,
          step,
        })

        walletAction?.onClose()
      },
    })
  }, [step, canGoBack])

  return (
    <FiatSendProvider>
      <Box width="full">
        {step === FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR && (
          <ContactOrBeneficiarySelector
            onContactOrBeneficiaryClick={handleOnContactOrBeneficiaryClick}
          />
        )}
        {step === FiatSendSteps.CONTACT_BANK_ACCOUNT && (
          <ContactBankAccountForm setStep={setStep} isLoadingWallet={isLoadingWallet} />
        )}
        {step === FiatSendSteps.BENEFICIARY && (
          <BeneficiaryForm setStep={setStep} isLoadingWallet={isLoadingWallet} />
        )}
        {step === FiatSendSteps.DETAILS && <PaymentDetails setStep={setStep} />}
        {step === FiatSendSteps.CONFIRMATION && <Confirmation setStep={setStep} />}
        {step === FiatSendSteps.EOF && <Complete />}
      </Box>
    </FiatSendProvider>
  )
}

export default memo(FiatSend)
